/**
 * What a face does when a finger lands on it.
 *
 * The owner's ask of 2026-09-04, evening: a face must answer a finger, and
 * answer differently depending on whose it is. One's own face **preens** - it
 * is being admired - and somebody else's **recoils**, «ты чего меня трогаешь».
 * The difference between the two is the whole of the idea; a single reaction
 * for both would have been a button that does nothing.
 *
 * The arithmetic is here and the movement is in `index.css`, because none of
 * this needs a DOM to be right: what is decided is which of four runs plays and
 * how wide it goes, and both are functions of things the app already knows.
 *
 * **Nothing leaves the device.** Somebody else's face recoils on this screen
 * only - no request, no notification, nothing stored, and the owner of the face
 * never learns. It is the cheapest thing in the app: a reaction that costs a
 * round trip would not be worth having.
 */

import type { Smile } from './Avatar';
import { LADDER } from './ladder';

export type TapMove = 'preen' | 'recoil' | 'burst' | 'wilt';

export const TAP_DURATION: Record<TapMove, number> = {
  preen: 560,
  recoil: 520,
  burst: 820,
  wilt: 900,
};

/** Taps further apart than this start a new run. */
export const TAP_CHAIN_MS = 1200;

/** Each further tap in a run is this much wider, and never wider than the cap. */
const TAP_WIDENING = 0.13;
const TAP_CAP = 1.75;

/** The tap a run climaxes on. */
const TAP_CLIMAX = 5;

/**
 * How hard a face answers a finger: the mood, and nothing else.
 *
 * The same figure the smile already carries, read once more - a face that has
 * been coming answers wholeheartedly and one that has not answers halfway,
 * which is the whole argument for putting a reaction on the tap at all. It is
 * one more place where the difference between «ходит» and «не ходит» can be
 * felt, and unlike the mouth it costs nothing to say it there.
 */
export function tapAmplitude(smile: Smile, away: boolean): number {
  // A pause first, because a paused face has no mouth to read the mood off.
  if (away) return 0.35;
  if (smile === 'wide') return 1.15;
  if (smile === 'smile' || smile === 'half') return 1;
  if (smile === 'flat') return 0.72;
  return 0.45;
}

/**
 * One tap, planned.
 *
 * `n` is how many taps this run holds, 1 for the first. The fifth is the
 * climax, and which climax it is the streak decides: a live one of five or more
 * bursts, a face without one only sags - the same reward-for-a-streak rule the
 * eyes and the colours follow, said in movement instead of in a gift. A paused
 * face never does either.
 */
export function planTap(o: {
  n: number;
  mine: boolean;
  smile: Smile;
  away: boolean;
  streak: number;
}): { move: TapMove; duration: number; amplitude: number } {
  const climax = o.n >= TAP_CLIMAX && !o.away;
  const move: TapMove = o.away
    ? 'wilt'
    : climax
      ? o.streak >= LADDER[0].at
        ? 'burst'
        : 'wilt'
      : o.mine
        ? 'preen'
        : 'recoil';
  const amplitude = Math.min(
    tapAmplitude(o.smile, o.away) * (1 + TAP_WIDENING * (o.n - 1)),
    TAP_CAP,
  );
  // Rounded, because the number goes into a custom property that ends up in a
  // `calc()` on every keyframe: two decimals are past what an eye can see and
  // keep the inline style from changing length as a run goes on.
  return { move, duration: TAP_DURATION[move], amplitude: Math.round(amplitude * 100) / 100 };
}

/**
 * Which tap of a run this one is: the one after `n`, or the first of a new run.
 *
 * A run and not a counter cleared by a timer: what makes five taps a run is
 * that they came one after another, and the only thing needed to know that is
 * when the last one was. A timer would also have to be cancelled on unmount,
 * and a face that is scrolled away and back is not in the middle of anything.
 */
export function nextTapIndex(n: number, previous: number, now: number): number {
  return now - previous <= TAP_CHAIN_MS ? n + 1 : 1;
}

/**
 * Whether the person asked for less movement.
 *
 * Read at the moment of the tap rather than once at startup: the setting can
 * change under a running app, and a face that kept moving after somebody turned
 * it off would be the app ignoring the only thing they said about this.
 */
export function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
